import React, { useState, useCallback } from 'react';
import { useApp } from '@/context/AppContext';
import {
  Users, UserPlus, EnvelopeSimple, ArrowClockwise,
  Eye, PaperPlaneTilt, Prohibit, CheckCircle, Trash,
  X, CaretDown, CaretUp, Warning, ShieldCheck, FileText,
  User, Clock, MagnifyingGlass, ChartBar,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

// ── helpers ────────────────────────────────────────────────────────────────

function fmt(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }); }
  catch { return '—'; }
}

function fmtFull(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return '—'; }
}

function StatusBadge({ status }) {
  return status === 'active'
    ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"><CheckCircle size={10} weight="fill" />Active</span>
    : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-red-500/10 text-red-400 border border-red-500/20"><Prohibit size={10} weight="fill" />Disabled</span>;
}

function RoleBadge({ role }) {
  return role === 'admin'
    ? <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-amber-500/10 text-amber-500 border border-amber-500/20">Admin</span>
    : <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-blue-500/10 text-blue-400 border border-blue-500/20">User</span>;
}

// ── InviteModal ────────────────────────────────────────────────────────────

function InviteModal({ prefillEmail = '', onClose, onSuccess }) {
  const { inviteUser } = useApp();
  const [email, setEmail] = useState(prefillEmail);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const res = await inviteUser(email, note);
      setResult(res);
      toast.success(`Invite sent to ${email}`);
      onSuccess?.();
    } catch {
      toast.error('Failed to send invite');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Overlay onClose={onClose}>
      <div className="w-full max-w-md">
        <ModalHeader title="Send Invite" onClose={onClose} />
        {result ? (
          <div className="p-6 space-y-4">
            <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-xs text-emerald-400">
              {result.message}
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Registration link</label>
              <div className="flex gap-2 items-center">
                <code className="flex-1 text-xs text-amber-400 bg-black/40 px-3 py-2 rounded break-all">
                  {result.reg_link}
                </code>
                <button
                  onClick={() => { navigator.clipboard.writeText(result.reg_link); toast.info('Copied'); }}
                  className="px-3 py-2 bg-white/5 hover:bg-white/10 rounded text-xs text-zinc-300 shrink-0"
                >
                  Copy
                </button>
              </div>
            </div>
            <div className="text-[10px] text-zinc-500">Expires: {fmtFull(result.expires_at)}</div>
            <button onClick={onClose} className="w-full py-2 bg-white/5 hover:bg-white/10 rounded text-sm">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Email *</label>
              <input
                type="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                readOnly={!!prefillEmail}
                className="w-full bg-[#121217] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-amber-500/50 outline-none disabled:opacity-60 read-only:opacity-70"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Note (optional)</label>
              <textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                rows={3}
                placeholder="Context for the recipient..."
                className="w-full bg-[#121217] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-amber-500/50 outline-none resize-none"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              className="w-full py-2 bg-amber-500 hover:bg-amber-600 text-black font-bold rounded text-sm disabled:opacity-50"
            >
              {busy ? 'Sending…' : 'Send Invitation'}
            </button>
          </form>
        )}
      </div>
    </Overlay>
  );
}

// ── DeleteModal ────────────────────────────────────────────────────────────

function DeleteModal({ user, onClose, onDeleted }) {
  const { deleteAdminUser } = useApp();
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await deleteAdminUser(user.id, typed);
      toast.success(`User ${user.email} permanently deleted`);
      onDeleted();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Overlay onClose={onClose}>
      <div className="w-full max-w-md">
        <ModalHeader title="Delete User" onClose={onClose} />
        <div className="p-6 space-y-4">
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3">
            <Warning size={18} className="text-red-400 shrink-0 mt-0.5" weight="fill" />
            <p className="text-xs text-red-300">
              This permanently deletes <strong className="text-white">{user.email}</strong> and all
              their personas, jobs, documents, searches, alerts, watchlist, and pgvector embeddings.
              <strong className="text-red-400"> This cannot be undone.</strong>
            </p>
          </div>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">
                Type <span className="text-white font-mono">{user.email}</span> to confirm
              </label>
              <input
                type="text"
                value={typed}
                onChange={e => setTyped(e.target.value)}
                placeholder={user.email}
                className="w-full bg-[#121217] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-red-500/50 outline-none font-mono"
              />
            </div>
            <button
              type="submit"
              disabled={busy || typed !== user.email}
              className="w-full py-2 bg-red-600 hover:bg-red-700 text-white font-bold rounded text-sm disabled:opacity-40"
            >
              {busy ? 'Deleting…' : 'Permanently Delete'}
            </button>
          </form>
        </div>
      </div>
    </Overlay>
  );
}

// ── UserDetailModal ────────────────────────────────────────────────────────

function PersonaCard({ p, userId }) {
  const { adminGetResume } = useApp();
  const [resumeText, setResumeText] = useState(null);
  const [loadingResume, setLoadingResume] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const loadFullResume = async () => {
    setLoadingResume(true);
    try {
      const data = await adminGetResume(userId, p.id);
      setResumeText(data.resume_text);
    } catch {
      toast.error('Could not load resume');
    } finally {
      setLoadingResume(false);
    }
  };

  return (
    <div className="border border-white/5 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-full" style={{ backgroundColor: p.color || '#6366F1' }} />
          <div>
            <div className="text-sm font-medium text-white">{p.name}</div>
            <div className="text-[10px] text-zinc-500">{p.role_title || 'No role title'}</div>
          </div>
        </div>
        <div className="flex gap-2">
          {p.has_resume && <span className="text-[10px] px-2 py-0.5 bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded">Resume</span>}
        </div>
      </div>

      {/* Score distribution */}
      {p.job_count > 0 && (
        <div className="grid grid-cols-4 gap-2 text-center text-[10px]">
          {[['Jobs', p.job_count], ['Min', p.job_score_min ?? '—'], ['Avg', p.job_score_avg != null ? `${p.job_score_avg}%` : '—'], ['Max', p.job_score_max ?? '—']].map(([k, v]) => (
            <div key={k} className="bg-white/[0.03] rounded p-2">
              <div className="text-zinc-500">{k}</div>
              <div className="text-white font-mono mt-0.5">{v}</div>
            </div>
          ))}
        </div>
      )}

      {/* Last search */}
      {p.last_search_query && (
        <div className="text-[10px] text-zinc-500">
          Last search: <span className="text-zinc-300">{p.last_search_query}</span>
          <span className="ml-2 text-zinc-600">{fmt(p.last_search_at)}</span>
        </div>
      )}

      {/* Resume excerpt */}
      {p.has_resume && p.resume_excerpt && (
        <div className="space-y-2">
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Resume excerpt</div>
          <p className="text-xs text-zinc-400 font-mono bg-black/30 rounded p-2 line-clamp-3">{p.resume_excerpt}</p>
          {!resumeText && (
            <button
              onClick={loadFullResume}
              disabled={loadingResume}
              className="text-[10px] text-amber-400 hover:text-amber-300 flex items-center gap-1"
            >
              <FileText size={12} />{loadingResume ? 'Loading…' : 'View Full Resume'}
            </button>
          )}
          {resumeText && (
            <div className="space-y-1">
              <button onClick={() => setExpanded(e => !e)} className="text-[10px] text-zinc-500 hover:text-white flex items-center gap-1">
                {expanded ? <CaretUp size={10} /> : <CaretDown size={10} />}
                {expanded ? 'Collapse' : 'Expand full text'}
              </button>
              {expanded && (
                <pre className="text-[10px] text-zinc-300 font-mono bg-black/30 rounded p-3 max-h-64 overflow-y-auto whitespace-pre-wrap break-words">
                  {resumeText}
                </pre>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function UserDetailModal({ user, onClose, onAction }) {
  const { getAdminUser, disableUser, enableUser } = useApp();
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoadingDetail(true);
    try {
      const d = await getAdminUser(user.id);
      setDetail(d);
    } catch {
      toast.error('Could not load user detail');
    } finally {
      setLoadingDetail(false);
    }
  }, [getAdminUser, user.id]);

  React.useEffect(() => { load(); }, [load]);

  const handleDisable = async () => {
    setBusy('disable');
    try {
      await disableUser(user.id);
      toast.success('User disabled');
      setDetail(d => d ? { ...d, status: 'disabled' } : d);
      onAction();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally { setBusy(''); }
  };

  const handleEnable = async () => {
    setBusy('enable');
    try {
      await enableUser(user.id);
      toast.success('User re-enabled');
      setDetail(d => d ? { ...d, status: 'active' } : d);
      onAction();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally { setBusy(''); }
  };

  const isActive = detail?.status === 'active';

  return (
    <>
      <Overlay onClose={onClose}>
        <div className="w-full max-w-2xl max-h-[90vh] flex flex-col">
          <ModalHeader title="User Profile" onClose={onClose} />
          {loadingDetail ? (
            <div className="p-10 text-center text-zinc-500 text-sm">Loading…</div>
          ) : !detail ? (
            <div className="p-10 text-center text-zinc-500 text-sm">Could not load user data</div>
          ) : (
            <div className="flex-1 overflow-y-auto">
              {/* Identity */}
              <div className="p-6 border-b border-white/5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center text-lg font-bold text-white">
                      {detail.email.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="text-white font-semibold">{detail.email}</div>
                      <div className="text-[10px] font-mono text-zinc-500 mt-0.5">{detail.id}</div>
                      <div className="flex items-center gap-2 mt-2">
                        <StatusBadge status={detail.status} />
                        <RoleBadge role={detail.role} />
                      </div>
                    </div>
                  </div>
                  {/* Action buttons */}
                  <div className="flex flex-col gap-2 shrink-0">
                    <button
                      onClick={() => setShowInvite(true)}
                      className="flex items-center gap-2 px-3 py-1.5 bg-white/5 hover:bg-white/10 rounded text-xs text-zinc-300"
                    >
                      <PaperPlaneTilt size={13} /> Re-invite
                    </button>
                    {isActive ? (
                      <button
                        onClick={handleDisable}
                        disabled={busy === 'disable'}
                        className="flex items-center gap-2 px-3 py-1.5 bg-amber-500/10 hover:bg-amber-500/20 rounded text-xs text-amber-400 disabled:opacity-50"
                      >
                        <Prohibit size={13} /> {busy === 'disable' ? '…' : 'Disable'}
                      </button>
                    ) : (
                      <button
                        onClick={handleEnable}
                        disabled={busy === 'enable'}
                        className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 rounded text-xs text-emerald-400 disabled:opacity-50"
                      >
                        <CheckCircle size={13} /> {busy === 'enable' ? '…' : 'Enable'}
                      </button>
                    )}
                    <button
                      onClick={() => setShowDelete(true)}
                      className="flex items-center gap-2 px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 rounded text-xs text-red-400"
                    >
                      <Trash size={13} /> Delete
                    </button>
                  </div>
                </div>
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-4 border-b border-white/5">
                {[
                  ['Searches', detail.total_searches, MagnifyingGlass],
                  ['Jobs Scored', detail.total_jobs_scored, ChartBar],
                  ['Documents', detail.total_documents, FileText],
                  ['Invites Sent', detail.invites_sent_count, PaperPlaneTilt],
                ].map(([label, val, Icon]) => (
                  <div key={label} className="p-4 text-center border-r border-white/5 last:border-r-0">
                    <Icon size={14} className="text-zinc-500 mx-auto mb-1" />
                    <div className="text-lg font-bold text-white">{val ?? 0}</div>
                    <div className="text-[10px] text-zinc-500">{label}</div>
                  </div>
                ))}
              </div>

              {/* Timeline */}
              <div className="p-6 border-b border-white/5">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-3 flex items-center gap-2"><Clock size={12} /> Account Timeline</div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  {[
                    ['Registered', detail.timeline?.registered_at],
                    ['Last Login', detail.timeline?.last_login_at],
                    ['Password Changed', detail.timeline?.password_changed_at],
                  ].map(([k, v]) => (
                    <div key={k} className="bg-white/[0.03] rounded p-3">
                      <div className="text-zinc-500 text-[10px]">{k}</div>
                      <div className="text-zinc-200 mt-0.5">{fmtFull(v)}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Personas */}
              <div className="p-6">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-3 flex items-center gap-2">
                  <User size={12} /> Personas ({detail.personas?.length || 0})
                </div>
                {detail.personas?.length > 0 ? (
                  <div className="space-y-3">
                    {detail.personas.map(p => (
                      <PersonaCard key={p.id} p={p} userId={detail.id} />
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-zinc-600 italic">No personas created yet</p>
                )}
              </div>
            </div>
          )}
        </div>
      </Overlay>

      {showInvite && (
        <InviteModal
          prefillEmail={user.email}
          onClose={() => setShowInvite(false)}
          onSuccess={() => setShowInvite(false)}
        />
      )}
      {showDelete && detail && (
        <DeleteModal
          user={detail}
          onClose={() => setShowDelete(false)}
          onDeleted={() => { onAction(); onClose(); }}
        />
      )}
    </>
  );
}

// ── Shared modal primitives ────────────────────────────────────────────────

function Overlay({ children, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-[#111111] border border-white/10 rounded-xl shadow-2xl w-full overflow-hidden">
        {children}
      </div>
    </div>
  );
}

function ModalHeader({ title, onClose }) {
  return (
    <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
      <h3 className="text-sm font-bold text-white">{title}</h3>
      <button onClick={onClose} className="text-zinc-500 hover:text-white transition-colors p-1">
        <X size={18} />
      </button>
    </div>
  );
}

// ── Main AdminUsersTab ──────────────────────────────────────────────────────

export default function AdminUsersTab() {
  const { listUsers, disableUser, enableUser } = useApp();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [invitePrefill, setInvitePrefill] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busyId, setBusyId] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listUsers();
      setUsers(res.users || []);
    } catch {
      toast.error('Could not load users');
    } finally {
      setLoading(false);
    }
  }, [listUsers]);

  React.useEffect(() => { load(); }, [load]);

  const openInvite = (email = '') => {
    setInvitePrefill(email);
    setShowInvite(true);
  };

  const handleDisableToggle = async (u) => {
    setBusyId(u.id);
    try {
      if (u.status === 'active') {
        await disableUser(u.id);
        toast.success(`${u.email} disabled`);
      } else {
        await enableUser(u.id);
        toast.success(`${u.email} re-enabled`);
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Action failed');
    } finally {
      setBusyId('');
    }
  };

  const filtered = users.filter(u =>
    !search || u.email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3">
        <div className="relative flex-1 max-w-xs">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            type="text"
            placeholder="Filter by email…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full bg-[#121217] border border-white/10 rounded-lg pl-9 pr-4 py-2 text-sm focus:border-amber-500/50 outline-none"
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className={`p-2 hover:bg-white/5 rounded-full transition-colors ${loading ? 'animate-spin' : ''}`}
          >
            <ArrowClockwise size={16} />
          </button>
          <button
            onClick={() => openInvite('')}
            className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-black px-4 py-2 rounded-lg text-sm font-bold transition-colors"
          >
            <UserPlus size={16} /> Invite User
          </button>
        </div>
      </div>

      {/* Summary chips */}
      <div className="flex gap-3">
        {[
          ['Total', users.length, 'text-zinc-400'],
          ['Active', users.filter(u => u.status === 'active').length, 'text-emerald-400'],
          ['Disabled', users.filter(u => u.status === 'disabled').length, 'text-red-400'],
          ['With Resume', users.filter(u => u.has_resume).length, 'text-blue-400'],
        ].map(([label, count, cls]) => (
          <div key={label} className="bg-white/[0.03] border border-white/5 rounded-lg px-4 py-2 text-center">
            <div className={`text-lg font-bold ${cls}`}>{count}</div>
            <div className="text-[10px] text-zinc-500">{label}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="jp-card overflow-hidden">
        <div className="px-6 py-3 border-b border-white/5">
          <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 flex items-center gap-2">
            <Users size={12} /> {filtered.length} user{filtered.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="bg-white/[0.02] text-zinc-500">
                <th className="px-4 py-3 font-semibold">User</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Personas</th>
                <th className="px-4 py-3 font-semibold hidden lg:table-cell">Last Search</th>
                <th className="px-4 py-3 font-semibold hidden xl:table-cell text-right">Searches</th>
                <th className="px-4 py-3 font-semibold hidden xl:table-cell text-right">Jobs</th>
                <th className="px-4 py-3 font-semibold hidden xl:table-cell text-right">Docs</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-zinc-600 italic">
                    {loading ? 'Loading…' : 'No users found'}
                  </td>
                </tr>
              )}
              {filtered.map(u => (
                <tr key={u.id} className="hover:bg-white/[0.02] transition-colors group">
                  {/* User */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center text-xs font-bold text-white shrink-0">
                        {u.email.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="text-white font-medium">{u.email}</div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <RoleBadge role={u.role} />
                          <span className="text-zinc-600 font-mono">{u.id?.split('-')[0]}</span>
                        </div>
                        <div className="text-zinc-600 mt-0.5">Joined {fmt(u.created_at)}{u.last_login_at ? ` · Login ${fmt(u.last_login_at)}` : ''}</div>
                      </div>
                    </div>
                  </td>

                  {/* Status */}
                  <td className="px-4 py-3">
                    <StatusBadge status={u.status} />
                    {u.must_change_password && (
                      <div className="mt-1 text-[10px] text-amber-500 flex items-center gap-1">
                        <Warning size={10} /> Pending reset
                      </div>
                    )}
                  </td>

                  {/* Personas */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-zinc-300">{u.persona_count}</span>
                      {u.has_resume && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded">
                          Resume
                        </span>
                      )}
                    </div>
                  </td>

                  {/* Last search */}
                  <td className="px-4 py-3 hidden lg:table-cell">
                    {u.last_search_query ? (
                      <div>
                        <div className="text-zinc-300 truncate max-w-[160px]">{u.last_search_query}</div>
                        <div className="text-zinc-600">{fmt(u.last_search_at)}</div>
                      </div>
                    ) : <span className="text-zinc-600">—</span>}
                  </td>

                  {/* Counts */}
                  <td className="px-4 py-3 hidden xl:table-cell text-right font-mono text-zinc-400">{u.total_searches}</td>
                  <td className="px-4 py-3 hidden xl:table-cell text-right font-mono text-zinc-400">{u.total_jobs_scored}</td>
                  <td className="px-4 py-3 hidden xl:table-cell text-right font-mono text-zinc-400">{u.total_documents}</td>

                  {/* Actions */}
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <ActionBtn
                        icon={Eye}
                        label="View"
                        onClick={() => setSelectedUser(u)}
                        cls="text-zinc-400 hover:text-white"
                      />
                      <ActionBtn
                        icon={PaperPlaneTilt}
                        label="Re-invite"
                        onClick={() => openInvite(u.email)}
                        cls="text-blue-400 hover:text-blue-300"
                      />
                      <ActionBtn
                        icon={u.status === 'active' ? Prohibit : CheckCircle}
                        label={u.status === 'active' ? 'Disable' : 'Enable'}
                        onClick={() => handleDisableToggle(u)}
                        disabled={u.role === 'admin' || busyId === u.id}
                        cls={u.status === 'active' ? 'text-amber-400 hover:text-amber-300' : 'text-emerald-400 hover:text-emerald-300'}
                      />
                      <ActionBtn
                        icon={Trash}
                        label="Delete"
                        onClick={() => setDeleteTarget(u)}
                        disabled={u.role === 'admin'}
                        cls="text-red-400 hover:text-red-300"
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      {showInvite && (
        <InviteModal
          prefillEmail={invitePrefill}
          onClose={() => setShowInvite(false)}
          onSuccess={load}
        />
      )}
      {selectedUser && (
        <UserDetailModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onAction={load}
        />
      )}
      {deleteTarget && (
        <DeleteModal
          user={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={load}
        />
      )}
    </div>
  );
}

function ActionBtn({ icon: Icon, label, onClick, disabled, cls }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={label}
      className={`p-1.5 rounded hover:bg-white/5 transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${cls}`}
    >
      <Icon size={14} />
    </button>
  );
}
